import {
	checkPermissions as checkGeolocationPermissions,
	requestPermissions as requestGeolocationPermissions,
	watchPosition as watchGeolocationPosition,
	type Position,
	type PositionOptions,
	clearWatch as clearGeolocationWatch,
} from '@tauri-apps/plugin-geolocation';
import { asyncWatchPosition } from './geoLocationAsync';

import * as Haptics from '@tauri-apps/plugin-haptics';
import { isTauri } from '@tauri-apps/api/core';
import confetti from 'canvas-confetti';
import { type } from '@tauri-apps/plugin-os';
import { app } from '@tauri-apps/api';

const isNative = isTauri(); // true if iOS or Android
const osType = isNative ? type() : 'web';

// version to fall back to web gps on android
const gpsBadAndroidVersion = { major: 0, minor: 5, patch: 0 };

export const shouldUseNativeGPS = async () => {
	if (!isNative) return false;
	if (osType == 'ios') return true;
	if (osType == 'android') return await isVersionNewerThan(gpsBadAndroidVersion);
	// just in case fall back to web
	return false;
};

export async function debugGpsDetails() {
	return {
		native: isNative,
		osType,
		version: parseSemver(await app.getVersion()),
		versionIsNewerThan: await isVersionNewerThan(gpsBadAndroidVersion),
	};
}

export async function checkLocationPermissions() {
	if (!isNative) throw new Error('Only check permissions in app');

	let permissions = await checkGeolocationPermissions();
	if (permissions.location === 'prompt' || permissions.location === 'prompt-with-rationale') {
		permissions = await requestGeolocationPermissions(['location']);
	}
	return permissions;
}

export async function clearWatchPosition(watchId: number) {
	const useNativeGps = await shouldUseNativeGPS();
	if (useNativeGps) {
		await clearGeolocationWatch(watchId);
	} else {
		navigator.geolocation.clearWatch(watchId);
	}
}

export type WatchPositionCallback = (pos: Position | null, error?: GeolocationPositionError | null | string) => void;

export async function watchPosition(cb: WatchPositionCallback, options?: PositionOptions): Promise<number | { error: string }> {
	const safeOptions: PositionOptions = {
		enableHighAccuracy: options?.enableHighAccuracy || true,
		timeout: options?.timeout || 10_000,
		maximumAge: options?.maximumAge || 0,
	};

	try {
		const useNativeGps = await shouldUseNativeGPS();
		if (useNativeGps) {
			// App (Android/IOS)
			const permissions = await checkLocationPermissions();
			if (permissions.location !== 'granted') {
				throw new Error(`Location permissions not granted (${permissions.location})`);
			}
			return await watchGeolocationPosition(safeOptions, cb);
		} else {
			// Browser
			return await asyncWatchPosition(cb, safeOptions);
		}
	} catch (error: unknown) {
		console.warn('error', error);
		return { error: error instanceof Error ? error.message : 'unknown watchPosition error' };
	}
}

export type hapticStyle = 'error' | 'success' | 'warning' | 'heavy' | 'light' | 'medium' | 'soft' | 'rigid';

export function haptics(style: hapticStyle | number = 'success') {
	if (isNative) {
		switch (style) {
			case 'error':
			case 'success':
			case 'warning':
				Haptics.notificationFeedback(style.toLowerCase() as Haptics.NotificationFeedbackType);
				break;
			case 'heavy':
			case 'light':
			case 'medium':
			case 'soft':
			case 'rigid':
				Haptics.impactFeedback(style.toLowerCase() as Haptics.ImpactFeedbackStyle);
				break;
			default:
				Haptics.vibrate(200);
				break;
		}
	} else {
		// Fallback for web
		if (!navigator.vibrate) return console.warn('Unable to vibrate');
		if (typeof style == 'number') {
			navigator.vibrate(style);
		} else {
			const duration: { [key in hapticStyle]: number | number[] } = {
				error: [100, 20, 200],
				success: [100, 20, 100],
				warning: [200, 50, 100],
				light: 100,
				soft: 100,
				medium: 300,
				heavy: 500,
				rigid: 500,
			};
			navigator.vibrate(duration[style] || 200);
			const audio = new Audio('/sfx/notification-ping-4.mp3');
			audio.currentTime = 0;
			audio.play().catch((err) => console.warn('Could not play notification sound:', err));
			console.log('playing sound');
		}
	}
}

export function showConfetti() {
	const count = 200;
	const defaults = {
		origin: { y: 0.7 },
	};

	function fire(particleRatio: number, opts: confetti.Options | undefined) {
		return confetti({
			...defaults,
			...opts,
			particleCount: Math.floor(count * particleRatio),
		});
	}

	return Promise.allSettled([
		fire(0.25, { spread: 26, startVelocity: 55 }),
		fire(0.2, { spread: 60 }),
		fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 }),
		fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 }),
		fire(0.1, { spread: 120, startVelocity: 45 }),
	]);
}

export async function getAppVersion() {
	if (!isTauri()) return undefined;
	const rawVersion = await app.getVersion();

	return parseSemver(rawVersion);
}

export type SemVer = {
	semVer?: string;
	major: number;
	minor: number;
	patch: number;
	prerelease?: string;
	buildmetadata?: string;
};
export function parseSemver(version: string): SemVer {
	const [semVer, major, minor, patch, prerelease, buildmetadata] =
		// apparently official semver regex
		version.match(
			/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/,
		) ?? [];

	return { semVer, major: parseInt(major), minor: parseInt(minor), patch: parseInt(patch), prerelease, buildmetadata };
}

export async function isVersionNewerThan(compare: SemVer) {
	const version = await getAppVersion();
	return isSemverNewer(compare, version || { major: 0, minor: 0, patch: 0 });
}

//Just a simple major/minor/patch test
export function isSemverNewer(base: SemVer, compare: SemVer) {
	if (compare.major > base.major) return true;
	if (compare.major < base.major) return false;

	if (compare.minor > base.minor) return true;
	if (compare.minor < base.minor) return false;

	if (compare.patch > base.patch) return true;
	if (compare.patch < base.patch) return false;
	return false;
}
